// Extracted from EnhancedResults.js (structural refactor, July 2026).
// Analysis-result view components — no behavior change.
import React, { useState, useEffect, useMemo } from 'react';
import { ANOVATable, KVTable } from '@/components/results/SharedTables';

export function RBDView({ data }) {
  const isSingleTrait = !data?.per_trait_results;
  const traits = React.useMemo(() => {
    if (!data || isSingleTrait) return [null];
    return Object.keys(data.per_trait_results);
  }, [data, isSingleTrait]);
  const [activeTrait, setActiveTrait] = React.useState(null);

  React.useEffect(() => {
    if (traits[0] !== null && !activeTrait) setActiveTrait(traits[0]);
  }, [traits, activeTrait]);

  if (!data) return null;

  const traitData = isSingleTrait ? data : (data.per_trait_results?.[activeTrait] || {});
  const ct = data.comprehensive_table;

  const sigColor = (status) => {
    if (status === 'best') return 'bg-emerald-500 text-white font-bold';
    if (status === 'at_par') return 'bg-emerald-100 text-emerald-800';
    return '';
  };

  return (
    <div className="space-y-6" data-testid="rbd-view">
      {data.interpretation && (
        <div className="bg-[#EBF5FB] rounded-2xl p-5 border border-[#2980B9]/20">
          <p className="text-sm text-[#2C2A28] leading-relaxed whitespace-pre-line">{data.interpretation}</p>
        </div>
      )}

      {/* Chart */}
      {data.chart && (
        <div className="rounded-2xl border border-[#E8E6E1] overflow-hidden">
          <img src={`data:image/png;base64,${data.chart}`} alt="RBD Chart" className="w-full" data-testid="rbd-chart" />
        </div>
      )}

      {/* ===== COMPREHENSIVE SUMMARY TABLE ===== */}
      {ct && ct.traits && ct.traits.length > 0 && (
        <div className="overflow-x-auto" data-testid="comprehensive-table">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">
            Comprehensive Summary Table
            <span className="ml-2 text-[10px] font-normal text-[#8A8580]">
              (Green = significantly superior based on CD at 5%)
            </span>
          </h4>
          <table className="w-full text-xs border-collapse border border-[#D5D1CC]">
            <thead>
              <tr className="bg-[#2980B9] text-white">
                <th className="px-3 py-2 text-left border border-[#2471A3] font-semibold">Treatment</th>
                {ct.traits.map(t => (
                  <th key={t} className="px-3 py-2 text-center border border-[#2471A3] font-semibold">{t}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Treatment means rows */}
              {ct.treatments.map((trt, i) => (
                <tr key={trt} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F0F8FF]'}>
                  <td className="px-3 py-1.5 font-semibold border border-[#E8E6E1] text-[#2C2A28]">{trt}</td>
                  {ct.traits.map(trait => {
                    const val = ct.means?.[trt]?.[trait];
                    const sig = ct.significance_map?.[trait]?.[trt] || '';
                    return (
                      <td key={trait} className={`px-3 py-1.5 text-center font-mono border border-[#E8E6E1] ${sigColor(sig)}`}>
                        {typeof val === 'number' ? val.toFixed(2) : val ?? '-'}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {/* Footer rows: CV, CD, SEm, etc. */}
              {ct.footer && (() => {
                const footerKeys = ct.footer[ct.traits[0]] ? Object.keys(ct.footer[ct.traits[0]]) : [];
                return footerKeys.map((fk, fi) => (
                  <tr key={fk} className={fi === 0 ? 'border-t-2 border-[#2980B9] bg-[#EBF5FB]' : 'bg-[#EBF5FB]'}>
                    <td className="px-3 py-1.5 font-bold text-[#2980B9] border border-[#D5D1CC]">{fk}</td>
                    {ct.traits.map(trait => {
                      const fval = ct.footer?.[trait]?.[fk];
                      return (
                        <td key={trait} className="px-3 py-1.5 text-center font-mono font-semibold text-[#2980B9] border border-[#D5D1CC]">
                          {typeof fval === 'number' ? fval.toFixed(4) : fval ?? '-'}
                        </td>
                      );
                    })}
                  </tr>
                ));
              })()}
            </tbody>
          </table>
          {/* Legend */}
          <div className="flex items-center gap-4 mt-2 text-[10px] text-[#8A8580]">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500 inline-block"></span> Best / Highest mean</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300 inline-block"></span> At par with best (diff &lt; CD 5%)</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-white border border-gray-300 inline-block"></span> Significantly inferior</span>
          </div>
        </div>
      )}

      {/* Multi-trait tab selector for per-trait details */}
      {!isSingleTrait && traits.length > 1 && traits[0] !== null && (
        <div>
          <h4 className="font-semibold text-[#2C2A28] mb-2 text-sm uppercase tracking-wider">Per-Trait Details</h4>
          <div className="flex flex-wrap gap-2 mb-4" data-testid="rbd-trait-tabs">
            {traits.map(t => (
              <button key={t} onClick={() => setActiveTrait(t)}
                className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all ${activeTrait === t ? 'bg-[#2980B9] text-white' : 'bg-white text-[#5E5A56] border border-[#E8E6E1] hover:border-[#2980B9]'}`}
                data-testid={`rbd-trait-tab-${t}`}
              >{t}</button>
            ))}
          </div>
        </div>
      )}

      {/* ANOVA Table */}
      <ANOVATable anovaTable={traitData.anova_table} title={isSingleTrait ? 'ANOVA Table' : `ANOVA – ${activeTrait}`} color="#2980B9" />

      {/* Statistical Parameters */}
      {traitData.statistical_parameters && (
        <div data-testid="rbd-stat-params">
          <h4 className="font-semibold text-[#2C2A28] mb-2 text-sm uppercase tracking-wider">Statistical Parameters</h4>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {Object.entries(traitData.statistical_parameters).map(([k, v]) => (
              <div key={k} className="bg-[#2980B9]/5 rounded-xl px-3 py-3 text-center border border-[#2980B9]/15">
                <div className="text-lg font-bold text-[#2980B9]">{typeof v === 'number' ? v.toFixed(4) : v}{k.includes('%') ? '%' : ''}</div>
                <div className="text-[10px] text-[#8A8580] uppercase">{k}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Genetic Parameters */}
      {traitData.genetic_parameters && (
        <div data-testid="rbd-genetic-params">
          <h4 className="font-semibold text-[#2C2A28] mb-2 text-sm uppercase tracking-wider">Genetic Parameters</h4>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {Object.entries(traitData.genetic_parameters).map(([k, v]) => (
              <div key={k} className="bg-[#27AE60]/5 rounded-xl px-3 py-3 text-center border border-[#27AE60]/15">
                <div className="text-lg font-bold text-[#27AE60]">{typeof v === 'number' ? v.toFixed(4) : v}{k.includes('%') ? '%' : ''}</div>
                <div className="text-[10px] text-[#8A8580] uppercase">{k}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   NC DESIGN VIEW (for NC Design II results)
============================================================ */


export function NCDesignView({ data }) {
  const traitMap = React.useMemo(() => {
    if (!data) return {};
    // NC Design II puts results in data.traits, flatten for rendering
    return data.traits || {};
  }, [data]);
  const traits = Object.keys(traitMap).filter(k => traitMap[k]?.anova_table);
  const [activeTrait, setActiveTrait] = React.useState(null);

  React.useEffect(() => {
    if (traits.length > 0 && !activeTrait) setActiveTrait(traits[0]);
  }, [traits, activeTrait]);

  if (!data) return null;
  if (traits.length === 0) return <GenericAnalysisView data={data} sectionKey="nc_design_ii" />;

  const traitData = traitMap[activeTrait] || {};

  return (
    <div className="space-y-6" data-testid="nc-design-view">
      {data.interpretation && (
        <div className="bg-[#EBF5FB] rounded-2xl p-5 border border-[#2980B9]/20">
          <p className="text-sm text-[#2C2A28] leading-relaxed whitespace-pre-line">{data.interpretation}</p>
        </div>
      )}

      {traits.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {traits.map(t => (
            <button key={t} onClick={() => setActiveTrait(t)}
              className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all ${activeTrait === t ? 'bg-[#2C3E50] text-white' : 'bg-white text-[#5E5A56] border border-[#E8E6E1] hover:border-[#2C3E50]'}`}
            >{t}</button>
          ))}
        </div>
      )}

      {data.chart && (
        <div className="rounded-2xl border border-[#E8E6E1] overflow-hidden">
          <img src={`data:image/png;base64,${data.chart}`} alt="NC Design Chart" className="w-full" />
        </div>
      )}

      <ANOVATable anovaTable={traitData.anova_table} title={`ANOVA – ${activeTrait}`} color="#2C3E50" />
      <KVTable data={traitData.variance_components} title="Variance Components" color="#2C3E50" />
    </div>
  );
}


/* ============================================================
   AMMI ANALYSIS VIEW
============================================================ */
